import React, { useState, useEffect, useCallback } from 'react';
import { syncManager } from '../services/sync';
import { useBilling } from '../context/BillingContext';
import { config } from '../config';
import RefundModal from '../components/admin/RefundModal';
import LogExpenseModal from '../components/admin/LogExpenseModal';
import StatementModal from '../components/admin/StatementModal';
import InvoiceModal from '../components/admin/InvoiceModal';
import LogPaymentModal from '../components/admin/LogPaymentModal';
import TransactionDetailModal from '../components/admin/TransactionDetailModal';
import {
    CreditCard,
    TrendingUp,
    AlertCircle,
    CheckCircle2,
    Upload,
    Search,
    Filter,
    RefreshCcw,
    FileText,
    DollarSign,
    ExternalLink,
    PieChart,
    Plus,
    Download,
    Activity,
    Users,
    Clock,
    ShieldCheck,
    Calendar,
    ChevronDown,
    Trash2,
    XCircle,
    Briefcase,
    Zap,
    Edit
} from 'lucide-react';

const BillingHubView = ({ managedClient }: any) => {
    const { trialDaysRemaining, getStatusDisplay } = useBilling();
    const [user, setUser] = useState<any>(null);
    const [role, setRole] = useState('CLIENT');
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({
        pendingVerification: 0,
        todayRevenue: 0,
        monthlyRecurring: 0,
        outstanding: 0,
        totalExpenses: 0,
        netProfit: 0,
        coachingCredits: 0,
        accountCredit: 0,
        pipelineValue: 0,
        totalCommission: 0,
        aging: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 }
    });
    const [transactions, setTransactions] = useState<any[]>([]);
    const [activeModal, setActiveModal] = useState<any>(null);
    const [selectedTx, setSelectedTx] = useState<any>(null);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [transactionDetailModalOpen, setTransactionDetailModalOpen] = useState(false);
    const [selectedTxForDetail, setSelectedTxForDetail] = useState<any>(null);
    const [viewMode, setViewMode] = useState('personal');
    const [uploading, setUploading] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const [expandedSections, setExpandedSections] = useState({ invoices: true, quotes: false, receipts: false, commissions: false });

    useEffect(() => {
        const email = localStorage.getItem(config.USER_EMAIL_KEY);
        const userRole = managedClient?.role || localStorage.getItem(config.USER_ROLE_KEY) || 'CLIENT';
        setUser(email);
        setRole(userRole);
        setViewMode(userRole === 'ADMIN' && !managedClient ? 'management' : 'personal');
    }, [managedClient]);

    useEffect(() => {
        fetchData();
    }, [viewMode, managedClient]);

    /**
     * Calculate all billing metrics from transaction data
     * This provides a fallback when backend doesn't provide pre-computed metrics
     */
    const calculateMetrics = useCallback((txData: any[]) => {
        const parseAmt = (v: any) => parseFloat(String(v || 0).replace(/[^-0-9.]/g, '')) || 0;
        const today = new Date();
        const todayStr = today.toISOString().slice(0, 10);
        const currentMonth = today.toISOString().slice(0, 7); // YYYY-MM
        const currentYear = today.toISOString().slice(0, 4); // YYYY

        // ==================== INVOICES ====================
        const invoices = txData.filter(t => t.type === 'Invoice');
        const invoiceMetrics = invoices.reduce((acc, inv) => {
            acc.total += parseAmt(inv.amount);
            acc.balance += parseAmt(inv.balance || inv.amount);
            if (inv.status?.toLowerCase() === 'overdue') {acc.overdue++;}
            if (inv.status === 'Paid') {acc.paidCount++;}
            if (inv.status?.toLowerCase() === 'pending' || inv.status === 'Sent') {acc.pendingCount++;}
            if (inv.status?.toLowerCase() === 'void') {acc.voidCount++;}
            return acc;
        }, { total: 0, balance: 0, overdue: 0, paidCount: 0, pendingCount: 0, voidCount: 0 });

        // Invoice average value
        const invoiceAvgValue = invoices.length > 0 ? invoiceMetrics.total / invoices.length : 0;

        // ==================== QUOTES ====================
        const quotes = txData.filter(t => t.type === 'Quote' || t.invoiceId?.startsWith('QT'));
        const quoteMetrics = quotes.reduce((acc, q) => {
            acc.total += parseAmt(q.amount);
            if (q.status === 'Active' || q.status === 'Sent') {acc.active++;}
            if (q.status === 'Accepted' || q.status === 'Converted') {acc.accepted++;}
            if (q.status === 'Expired') {acc.expired++;}
            return acc;
        }, { total: 0, active: 0, accepted: 0, expired: 0 });
        const quoteAvgValue = quotes.length > 0 ? quoteMetrics.total / quotes.length : 0;

        // ==================== RECEIPTS ====================
        const receipts = txData.filter(t => t.type === 'Receipt' || t.invoiceId?.startsWith('RCPT'));
        const receiptMetrics = receipts.reduce((acc, r) => {
            acc.total += parseAmt(r.amount);
            if (r.allocatedToInvoice) {acc.allocated += parseAmt(r.amount);}
            else {acc.unallocated += parseAmt(r.amount);}
            return acc;
        }, { total: 0, allocated: 0, unallocated: 0 });

        // Receipts MTD/YTD
        const receiptMTD = receipts.filter(r => {
            const date = r.date || r.createdAt;
            if (!date) {return false;}
            try { return new Date(date).toISOString().slice(0, 7) === currentMonth; } catch { return false; }
        }).reduce((sum, r) => sum + parseAmt(r.amount), 0);

        const receiptYTD = receipts.filter(r => {
            const date = r.date || r.createdAt;
            if (!date) {return false;}
            try { return new Date(date).toISOString().slice(0, 4) === currentYear; } catch { return false; }
        }).reduce((sum, r) => sum + parseAmt(r.amount), 0);

        const receiptAvgValue = receipts.length > 0 ? receiptMetrics.total / receipts.length : 0;

        // Receipts by method
        const receiptByMethod = {
            EFT: receipts.filter(r => (r.method || '').toLowerCase().includes('eft')).reduce((sum, r) => sum + parseAmt(r.amount), 0),
            Card: receipts.filter(r => (r.method || '').toLowerCase().includes('card')).reduce((sum, r) => sum + parseAmt(r.amount), 0),
            Cash: receipts.filter(r => (r.method || '').toLowerCase().includes('cash')).reduce((sum, r) => sum + parseAmt(r.amount), 0),
            Other: receipts.filter(r => !r.method || (!r.method.toLowerCase().includes('eft') && !r.method.toLowerCase().includes('card') && !r.method.toLowerCase().includes('cash'))).reduce((sum, r) => sum + parseAmt(r.amount), 0)
        };

        // ==================== REFUNDS ====================
        const refunds = txData.filter(t =>
            t.type === 'Refund' ||
            t.type === 'Credit Note' ||
            (t.invoiceId && (t.invoiceId.startsWith('REF') || t.invoiceId.startsWith('CN')))
        );

        const refundsMTD = refunds.filter(r => {
            const date = r.date || r.createdAt;
            if (!date) {return false;}
            try { return new Date(date).toISOString().slice(0, 7) === currentMonth; } catch { return false; }
        }).reduce((sum, r) => sum + parseAmt(r.Refund_Amount || r.amount), 0);

        const refundsYTD = refunds.filter(r => {
            const date = r.date || r.createdAt;
            if (!date) {return false;}
            try { return new Date(date).toISOString().slice(0, 4) === currentYear; } catch { return false; }
        }).reduce((sum, r) => sum + parseAmt(r.Refund_Amount || r.amount), 0);

        const refundTotal = refunds.reduce((sum, r) => sum + parseAmt(r.Refund_Amount || r.amount), 0);
        const refundAvgValue = refunds.length > 0 ? refundTotal / refunds.length : 0;

        const refundByType = {
            Full: refunds.filter(t => (t.Refund_Type || '').toLowerCase() === 'full').reduce((sum, t) => sum + parseAmt(t.Refund_Amount || t.amount), 0),
            Partial: refunds.filter(t => (t.Refund_Type || '').toLowerCase() === 'partial').reduce((sum, t) => sum + parseAmt(t.Refund_Amount || t.amount), 0),
            CreditNote: refunds.filter(t => t.type === 'Credit Note').reduce((sum, t) => sum + parseAmt(t.Refund_Amount || t.amount), 0)
        };

        // ==================== GLOBAL METRICS ====================
        const todayRevenue = receipts.filter(r => r.date === todayStr).reduce((sum, r) => sum + parseAmt(r.amount), 0);
        const pendingVerification = txData.filter(t => t.status?.toLowerCase().includes('pending') || t.pendingVerification).length;
        const totalExpenses = txData.filter(t => t.type === 'Expense' || t.Expense_Amount > 0).reduce((sum, t) => sum + parseAmt(t.Expense_Amount || t.amount), 0);
        const netPerformance = receiptMetrics.total - refundTotal - totalExpenses;

        // LTV Calculation
        const uniqueClients = [...new Set(txData.map(t => t.clientEmail).filter(Boolean))];
        const estimatedLTV = uniqueClients.length > 0
            ? (receiptMetrics.total + invoiceMetrics.paidCount > 0 ? receiptMetrics.total + invoiceMetrics.total : 0) / uniqueClients.length
            : 0;

        const totalCommission = txData.filter(t => t.Commission_Amount > 0).reduce((sum, t) => sum + parseAmt(t.Commission_Amount), 0);
        const pipelineValue = quoteMetrics.total;
        const coachingCredits = txData.filter(t => t.type === 'Coaching_Credit' || t.coachingSession === true).reduce((sum, t) => sum + (parseAmt(t.sessionsRemaining) || 0), 0);

        // ==================== AGING METRICS ====================
        const aging = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
        invoices.filter(t => t.status !== 'Paid').forEach(t => {
            const dueDate = t.dueDate ? new Date(t.dueDate) : null;
            if (dueDate) {
                const daysOverdue = Math.floor(((today as any) - (dueDate as any)) / (1000 * 60 * 60 * 24));
                if (daysOverdue > 0) {
                    const balance = parseAmt(t.balance || t.amount);
                    if (daysOverdue <= 30) {aging['0-30'] += balance;}
                    else if (daysOverdue <= 60) {aging['31-60'] += balance;}
                    else if (daysOverdue <= 90) {aging['61-90'] += balance;}
                    else {aging['90+'] += balance;}
                }
            }
        });

        // ==================== DUNNING METRICS ====================
        const dunning = { L1: 0, L2: 0, L3: 0 };
        invoices.filter(t => t.dunningLevel > 0).forEach(t => {
            if (t.dunningLevel >= 1) {dunning.L1++;}
            if (t.dunningLevel >= 2) {dunning.L2++;}
            if (t.dunningLevel >= 3) {dunning.L3++;}
        });

        // ==================== RETURN ALL METRICS ====================
        return {
            // EXISTING METRICS (preserved for backward compatibility)
            pendingVerification: pendingVerification,
            todayRevenue: todayRevenue,
            monthlyRecurring: 0,
            outstanding: invoiceMetrics.balance,
            totalExpenses: totalExpenses,
            netProfit: netPerformance,
            coachingCredits: coachingCredits,
            accountCredit: receiptMetrics.unallocated,
            pipelineValue: pipelineValue,
            totalCommission: totalCommission,
            aging: aging,

            // NEW GLOBAL METRICS
            totalReceivable: invoiceMetrics.balance,
            netPerformance: netPerformance,
            estimatedLTV: estimatedLTV,

            // INVOICE METRICS
            invoiceTotal: invoiceMetrics.total,
            invoicePaid: invoiceMetrics.total - invoiceMetrics.balance,
            invoiceBalance: invoiceMetrics.balance,
            invoiceOverdueCount: invoiceMetrics.overdue,
            invoiceOverdueValue: invoices.filter(t => t.status?.toLowerCase() === 'overdue').reduce((sum, t) => sum + parseAmt(t.balance || t.amount), 0),
            invoiceAvgValue: invoiceAvgValue,
            invoicePaidCount: invoiceMetrics.paidCount,
            invoicePendingCount: invoiceMetrics.pendingCount,
            invoiceVoidCount: invoiceMetrics.voidCount,

            // QUOTE METRICS
            quoteTotal: quoteMetrics.total,
            quoteActiveCount: quoteMetrics.active,
            quoteActiveValue: quotes.filter(q => q.status === 'Active' || q.status === 'Sent').reduce((sum, q) => sum + parseAmt(q.amount), 0),
            quoteAcceptedCount: quoteMetrics.accepted,
            quoteAcceptedValue: quotes.filter(q => q.status === 'Accepted' || q.status === 'Converted').reduce((sum, q) => sum + parseAmt(q.amount), 0),
            quoteExpiredCount: quoteMetrics.expired,
            quoteExpiredValue: quotes.filter(q => q.status === 'Expired').reduce((sum, q) => sum + parseAmt(q.amount), 0),
            quoteAvgValue: quoteAvgValue,

            // RECEIPT METRICS
            receiptTotal: receiptMetrics.total,
            receiptAllocated: receiptMetrics.allocated,
            receiptUnallocated: receiptMetrics.unallocated,
            receiptMTD: receiptMTD,
            receiptYTD: receiptYTD,
            receiptAvgValue: receiptAvgValue,
            receiptByMethod: receiptByMethod,

            // REFUND METRICS
            refundsMTD: refundsMTD,
            refundsYTD: refundsYTD,
            refundTotal: refundTotal,
            refundAvgValue: refundAvgValue,
            refundByType: refundByType,
            netImpact: receiptMetrics.total - refundTotal,

            // DUNNING METRICS
            dunning: dunning
        };
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const userRole = managedClient?.role || localStorage.getItem(config.USER_ROLE_KEY) || 'CLIENT';
            const email = managedClient?.email || localStorage.getItem(config.USER_EMAIL_KEY);
            const response = await syncManager.triggerWorkflow('billing-hub-v1', { role: userRole, email });

            // PRIMARY: Calculate all metrics from transactions (fallback when backend fails)
            const computedMetrics = calculateMetrics(response.transactions || transactions);

            // If backend provides metrics, merge them (allow backend to override calculated values)
            if (response?.metrics) {
                setMetrics({
                    // Spread all calculated metrics first
                    ...computedMetrics,
                    // Then override with any backend-provided metrics
                    ...response.metrics,
                    // Ensure aging and dunning are preserved from calculations if not provided by backend
                    aging: response.metrics.aging || computedMetrics.aging,
                    dunning: response.metrics.dunning || computedMetrics.dunning
                });
            } else {
                // Use only calculated metrics
                setMetrics(computedMetrics);
            }

            if (response?.transactions) {
                setTransactions(response.transactions);
            }
        } catch (error: any) {
            console.error('Failed to fetch billing data:', error);
            // FALLBACK: Calculate metrics from existing transactions on error
            setMetrics(prev => ({
                ...prev,
                ...calculateMetrics(transactions)
            }));
        } finally {
            setLoading(false);
        }
    }, [managedClient, viewMode, transactions]);

    const handleAction = async (action: any, tx: any) => {
        try {
            const txId = tx.invoiceId || tx.id;
            if (action === 'accept_quote') {
                if (confirm(`Accept Quote #${txId}? This will generate a Tax Invoice and trigger fulfillment process.`)) {
                    await syncManager.triggerWorkflow('hq-accept-quote', { invoiceId: txId });
                    alert('Success: Quote accepted and conversion triggered.');
                }
            } else if (action === 'approve') {
                if (confirm(`Approve Proof of Payment for #${txId}? This will mark record as Paid in Sovereign Ledger.`)) {
                    await syncManager.triggerWorkflow('billing-approve-pop', { invoiceId: txId });
                    alert('Success: Transaction verified and ledger updated.');
                }
            } else if (action === 'void') {
                if (confirm(`🚨 WARNING: Voiding Invoice #${txId} is permanent. Proceed?`)) {
                    await syncManager.triggerWorkflow('billing-void-transaction', { invoiceId: txId });
                    alert('Success: Transaction voided.');
                }
            } else if (action === 'delete') {
                const txType = tx.type || 'Transaction';
                if (confirm(`🚨 WARNING: Delete ${txType} #${txId}? This action cannot be undone.`)) {
                    // Use syncManager to trigger the billing delete workflow
                    await syncManager.triggerWorkflow('billing/process', {
                        action: 'delete',
                        invoiceId: txId,
                        type: txType
                    });
                    alert('Success: Transaction deleted.');
                    fetchData();
                }
            } else if (action === 'refund') {
                setSelectedTx(tx);
                setRefundModalOpen(true);
            }
            fetchData();
        } catch (error: any) {
            alert(`Sovereign Action Failed: ${error.message}`);
        }
    };

    const handleViewDetails = (tx: any) => {
        setSelectedTxForDetail(tx);
        setTransactionDetailModalOpen(true);
    };

    const handlePopUpload = async (e: any, tx: any) => {
        const file = e.target.files[0];
        if (!file) {return;}

        setUploading(true);
        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                if (typeof reader.result !== 'string') {return;}
                const base64String = reader.result.replace('data:', '').replace(/^.+,/, '');
                await syncManager.triggerWorkflow('billing/upload-pop', {
                    filename: `POP_${tx.invoiceId}_${Date.now()}.${file.name.split('.').pop()}`,
                    data: base64String,
                    invoiceId: tx.invoiceId,
                    targetClientEmail: tx.clientEmail || user
                });
                alert('Proof of Payment uploaded for verification.');
                fetchData();
            };
            reader.readAsDataURL(file);
        } catch (error: any) {
            alert('Upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const toggleSection = (section: any) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Data Filtering
    const categorized = {
        invoices: transactions.filter(t => t.type === 'Invoice' && t.invoiceId?.startsWith('INV-')),
        quotes: transactions.filter(t => t.type === 'Quote' || t.invoiceId?.startsWith('QT')),
        receipts: transactions.filter(t => t.type === 'Receipt' || t.invoiceId?.startsWith('RCPT')),
        commissions: transactions.filter(t => t.type === 'Commission' || t.Commission_Amount > 0)
    };

    // Sub-metrics
    const invoiceMetrics = categorized.invoices.reduce((acc, inv) => {
        acc.total += inv.amount;
        acc.balance += inv.balance || 0;
        if (inv.status?.toLowerCase() === 'overdue') {acc.overdue++;}
        return acc;
    }, { total: 0, balance: 0, overdue: 0 });

    const quoteMetrics = categorized.quotes.reduce((acc, q) => {
        acc.total += q.amount;
        if (q.status === 'Active' || q.status === 'Sent') {acc.active++;}
        return acc;
    }, { total: 0, active: 0 });

    const totalRealized = categorized.receipts.reduce((sum, r) => sum + r.amount, 0);

    // Refund Metrics
    const getMonthKey = (dateStr: any) => {
        if (!dateStr) {return '';}
        try {
            return new Date(dateStr).toISOString().slice(0, 7); // YYYY-MM
        } catch {
            return '';
        }
    };
    const currentMonthKey = new Date().toISOString().slice(0, 7);

    const refunds = transactions.filter(t =>
        t.type === 'Refund' ||
        t.type === 'Credit Note' ||
        (t.invoiceId && (t.invoiceId.startsWith('REF') || t.invoiceId.startsWith('CN')))
    );

    const refundsMonthToDate = refunds
        .filter(r => getMonthKey(r.date || r.createdAt) === currentMonthKey)
        .reduce((sum, r) => sum + parseAmt(r.amount || r.Refund_Amount), 0);

    const refundsToDate = refunds
        .reduce((sum, r) => sum + parseAmt(r.amount || r.Refund_Amount), 0);

    // Total Credits (Unallocated Receipts)
    const totalUnallocatedCredits = categorized.receipts
        .filter(r => !r.allocatedToInvoice)
        .reduce((sum, r) => sum + r.amount, 0);

    // Global Metrics
    const parseAmt = (v: any) => parseFloat(String(v || 0).replace(/[^-0-9.]/g, '')) || 0;
    const formatCurrencyLocal = (val: any) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(val || 0);

    return (
        <div className="space-y-8 pb-20">
            {/* Header Area */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                <div>
                    <h1 className="text-5xl font-black text-slate-900 tracking-tighter">Finance Hub</h1>
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mt-2">
                        {viewMode === 'management' ? 'Enterprise Operations' : `${role === 'ADMIN' ? 'Sovereign' : role} Portal`}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {['ADMIN', 'COACH'].includes(role) && !managedClient && (
                        <div className="bg-slate-100 p-1.5 rounded-2xl flex gap-1">
                            <button onClick={() => setViewMode('personal')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${viewMode === 'personal' ? 'bg-white shadow-lg text-slate-900' : 'text-slate-400'}`}>Personal</button>
                            <button onClick={() => setViewMode('management')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${viewMode === 'management' ? 'bg-white shadow-lg text-indigo-600' : 'text-slate-400'}`}>Management</button>
                        </div>
                    )}

                    {/* Operational Buttons */}
                    {(role === 'ADMIN' || (role === 'COACH' && viewMode === 'management')) && (
                        <div className="flex items-center gap-2">
                            <button onClick={() => setActiveModal('create_invoice')} className="bg-slate-900 text-white px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2">
                                <Plus size={16} /> New Record
                            </button>
                            <button onClick={() => setActiveModal('log_payment')} className="bg-emerald-600 text-white px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-100">
                                <DollarSign size={16} /> Log Payment
                            </button>
                            {role === 'ADMIN' && (
                                <button onClick={() => setActiveModal('log_expense')} className="bg-white border-2 border-slate-100 text-slate-900 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2">
                                    <PieChart size={16} /> Expense
                                </button>
                            )}
                            <button onClick={() => setActiveModal('gen_statement')} className="bg-white border-2 border-slate-100 text-indigo-700 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2">
                                <FileText size={16} /> Statement
                            </button>
                        </div>
                    )}

                    <button onClick={fetchData} className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 transition-all shadow-sm group">
                        <RefreshCcw className={`w-5 h-5 text-slate-500 group-hover:text-indigo-600 transition-colors ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    {viewMode === 'personal' && (
                        <button onClick={() => setShowInstructions(!showInstructions)} className={`px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 ${showInstructions ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white border-2 border-slate-100 text-slate-900 hover:bg-slate-50'}`}>
                            <CreditCard size={16} /> Banking
                        </button>
                    )}
                </div>
            </div>

            {/* Trial Context & Alerts */}
            {trialDaysRemaining > 0 && viewMode === 'personal' && (
                <div className={`mb-6 p-4 rounded-2xl border flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-500 ${trialDaysRemaining <= 3 ? 'bg-rose-50 border-rose-100 text-rose-900 shadow-rose-100/50' :
                    trialDaysRemaining <= 7 ? 'bg-amber-50 border-amber-100 text-amber-900 shadow-amber-100/50' :
                        'bg-indigo-50 border-indigo-100 text-indigo-900 shadow-indigo-100/50'
                    } shadow-sm border`}>
                    <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-xl ${trialDaysRemaining <= 3 ? 'bg-rose-100' :
                            trialDaysRemaining <= 7 ? 'bg-amber-100' :
                                'bg-indigo-100'
                            }`}>
                            <Zap className={trialDaysRemaining <= 3 ? 'animate-pulse' : ''} size={20} />
                        </div>
                        <div>
                            <h3 className="font-black uppercase tracking-tight text-sm">
                                {trialDaysRemaining <= 3 ? 'Critical Action Required: Trial Expiring' :
                                    trialDaysRemaining <= 7 ? 'Trial Status: Deep Dive Ready?' :
                                        'Welcome to BizBuilder Hub'}
                            </h3>
                            <p className="text-xs font-bold opacity-80">
                                {trialDaysRemaining <= 3 ? `Your access expires in ${trialDaysRemaining} days. Convert to a paid tier now to preserve your progress.` :
                                    trialDaysRemaining <= 7 ? `You are ${14 - trialDaysRemaining} days into your journey. Would you like a coaching check-in?` :
                                        `You have ${trialDaysRemaining} days of full access remaining. Explore Direct Settlement tools below.`}
                            </p>

                        </div>
                    </div>
                </div>
            )}

            {/* Banking Instructions */}
            {showInstructions && viewMode === 'personal' && (
                <div className="mb-8 bg-indigo-900 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-indigo-200/50 animate-in zoom-in-95 duration-500 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none">
                        <CreditCard size={120} />
                    </div>
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-black uppercase tracking-tighter mb-4 flex items-center gap-3">
                                <span className="p-2 bg-indigo-500 rounded-xl"><DollarSign size={20} /></span>
                                Direct Settlement
                            </h3>
                            <p className="text-indigo-200 font-bold text-sm leading-relaxed mb-6">
                                Use these details for EFT payments. Please use your <span className="text-white">Email Address</span> or <span className="text-white">Invoice Number</span> as reference for automated clearing.
                            </p>
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                                <table className="w-full text-sm">
                                    <tbody className="divide-y divide-white/10">
                                        <tr className="group"><td className="py-3 text-indigo-300 font-black uppercase text-[10px]">Bank</td><td className="py-3 text-right font-black">FNB / RMB</td></tr>
                                        <tr className="group"><td className="py-3 text-indigo-300 font-black uppercase text-[10px]">Acc Name</td><td className="py-3 text-right font-black">CHRYSA LINKS (PTY) LTD</td></tr>
                                        <tr className="group"><td className="py-3 text-indigo-300 font-black uppercase text-[10px]">ACC #</td><td className="py-3 text-right font-black">630321350170</td></tr>
                                        <tr className="group"><td className="py-3 text-indigo-300 font-black uppercase text-[10px]">Type</td><td className="py-3 text-right font-black">Cheque / Business</td></tr>
                                        <tr className="group"><td className="py-3 text-indigo-300 font-black uppercase text-[10px]">Ref</td><td className="py-3 text-right font-emerald-400 font-black italic">Your Email</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="flex flex-col justify-center">
                            <div className="bg-indigo-800/50 rounded-3xl p-6 border border-indigo-700">
                                <h4 className="font-black uppercase tracking-widest text-[10px] mb-4 text-indigo-300">Pop Upload Support</h4>
                                <ul className="space-y-4">
                                    <li className="flex gap-4">
                                        <div className="w-6 h-6 rounded-full bg-indigo-700 flex items-center justify-center text-[10px] font-black shrink-0">1</div>
                                        <p className="text-xs font-bold leading-normal">Fast-track verification by uploading POP directly in record below.</p>
                                    </li>
                                    <li className="flex gap-4">
                                        <div className="w-6 h-6 rounded-full bg-indigo-700 flex items-center justify-center text-[10px] font-black shrink-0">2</div>
                                        <p className="text-xs font-bold leading-normal">Our Sovereign Ledger audits bank records every 30 minutes.</p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <button className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm transition-all active:scale-95 ${trialDaysRemaining <= 3 ? 'bg-rose-600 text-white hover:bg-rose-700' :
                        'bg-white text-slate-900 hover:bg-slate-50 border border-slate-200'
                        }`}>
                        {trialDaysRemaining <= 3 ? 'Upgrade Now' : 'Request Help'}
                    </button>
                </div>
            )}

            {/* Global Intelligence Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {viewMode === 'management' ? (
                    <>
                        <MetricCard title="Today's Revenue" value={formatCurrencyLocal(metrics.todayRevenue)} icon={<TrendingUp size={20} />} color="text-emerald-600" bgColor="bg-emerald-50" trend="+12% from yesterday" />
                        <MetricCard title="Pending Verification" value={metrics.pendingVerification} icon={<AlertCircle size={20} />} color="text-amber-600" bgColor="bg-amber-50" trend="Action required" />
                        <MetricCard title="Monthly Recurring" value={formatCurrencyLocal(metrics.monthlyRecurring)} icon={<RefreshCcw size={20} />} color="text-blue-600" bgColor="bg-blue-50" trend="Active subscriptions" />
                        <MetricCard title="Total Outstanding" value={formatCurrencyLocal(metrics.outstanding)} icon={<AlertCircle size={20} />} color="text-rose-600" bgColor="bg-rose-50" trend="Awaiting payment" />
                        <MetricCard title="Net Performance" value={formatCurrencyLocal(metrics.netProfit)} icon={<Activity size={20} />} color="text-indigo-600" bgColor="bg-indigo-50" trend="Realized vs Expenses" />
                        <MetricCard title="Estimated LTV" value={formatCurrencyLocal((metrics as any).estimatedLTV)} icon={<Users size={20} />} color="text-blue-600" bgColor="bg-blue-50" trend="Avg LifeValue / Client" />
                        <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                            <div className="relative z-10">
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4">Debt Aging Intelligence</p>
                                <div className="space-y-3">
                                    {Object.entries(metrics.aging || {}).map(([range, val]) => (
                                        <div key={range} className="flex items-center justify-between group/row">
                                            <span className="text-[10px] font-black text-slate-500 uppercase">{range} Days</span>
                                            <div className="flex-1 mx-4 h-1 bg-slate-800 rounded-full overflow-hidden">
                                                <div className={`h-full ${range === '90+' ? 'bg-rose-500' : 'bg-indigo-500'} transition-all`} style={{ width: `${Math.min(100, (val / (metrics.outstanding || 1)) * 100)}%` }}></div>
                                            </div>
                                            <span className="text-[10px] font-black text-white">{formatCurrencyLocal(val)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="col-span-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <MetricCard title="Coaching Credits" value={(metrics as any).coaching_credits || metrics.coachingCredits} icon={<Users size={20} />} color="text-emerald-600" bgColor="bg-emerald-50" trend="Available sessions" />
                        <MetricCard title="Realized Performance" value={formatCurrencyLocal((metrics as any).totalRealized)} icon={<TrendingUp size={20} />} color="text-blue-600" bgColor="bg-blue-50" trend="Month inflow" />
                        <MetricCard title="Total Outstanding" value={formatCurrencyLocal(metrics.outstanding)} icon={<AlertCircle size={20} />} color="text-rose-600" bgColor="bg-rose-50" trend="Total due now" />
                        {trialDaysRemaining > 0 ? (
                            <MetricCard title="Trial Progress" value={`${14 - trialDaysRemaining} / 14 Days`} icon={<Zap size={20} />} color="text-indigo-600" bgColor="bg-indigo-50" trend={`Day ${14 - trialDaysRemaining} Performance`} />
                        ) : (
                            <MetricCard title="Unallocated" value={formatCurrencyLocal(metrics.accountCredit)} icon={<FileText size={20} />} color="text-indigo-600" bgColor="bg-indigo-50" trend="Account credit" />
                        )}
                        <MetricCard title="Verifying" value={metrics.pendingVerification} icon={<Clock size={20} />} color="text-amber-600" bgColor="bg-amber-50" trend="POP under review" />
                    </div>
                )}
            </div>

            {/* Payment Instructions Restoration */}
            <div className="bg-indigo-900 rounded-[2.5rem] p-8 text-white shadow-2xl overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-800 rounded-full -mr-20 -mt-20 blur-3xl opacity-50 group-hover:scale-110 transition-transform duration-1000" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-2xl font-black tracking-tighter mb-1">Direct Settlement Hub</h2>
                        <p className="text-indigo-200 text-xs font-bold uppercase tracking-widest">Sovereign Billing & Trust Details</p>
                    </div>
                    <button onClick={() => setShowInstructions(!showInstructions)} className="px-6 py-3 bg-white text-indigo-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2">
                        {showInstructions ? 'Hide Banking Details' : 'View Payment Instructions'}
                    </button>
                </div>
                {showInstructions && (
                    <div className="mt-8 pt-8 border-t border-indigo-800 grid grid-cols-1 md:grid-cols-3 gap-8 animate-in slide-in-from-top-4 duration-500">
                        <div>
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Banking Entity</p>
                            <p className="font-bold text-lg">First National Bank (FNB)</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Account Type</p>
                            <p className="font-bold text-lg">Business Cheque</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Reference Policy</p>
                            <p className="font-bold text-lg text-emerald-400 underline decoration-indigo-700 underline-offset-4">[YOUR_INVOICE_ID]</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Aging Debt Analysis (Management Only) */}
            {
                viewMode === 'management' && (
                    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                            <Activity size={16} className="text-indigo-500" /> Aging Debt Breakdown
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {['0-30', '31-60', '61-90', '90+'].map(period => (
                                <div key={period} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 transition-all hover:shadow-lg group">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{period} Days</p>
                                    <p className={`text-2xl font-black mt-1 ${period === '90+' ? 'text-rose-600' : 'text-slate-900'}`}>{formatCurrencyLocal(metrics.aging?.[period])}</p>
                                    <div className={`h-1 w-0 group-hover:w-full transition-all duration-500 mt-4 rounded-full ${period === '90+' ? 'bg-rose-500' : 'bg-indigo-500'}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* Invoices Section */}
            <Section
                title="Active Invoices"
                count={categorized.invoices.length}
                icon={<FileText />}
                color="indigo"
                isOpen={expandedSections.invoices}
                onToggle={() => toggleSection('invoices')}
                metrics={[
                    { label: 'Total Value', value: formatCurrencyLocal(invoiceMetrics.total) },
                    { label: 'Outstanding Balance', value: formatCurrencyLocal(invoiceMetrics.balance), color: 'text-rose-600' },
                    { label: 'Overdue Invoices', value: invoiceMetrics.overdue },
                    { label: 'Refunds MTD', value: formatCurrencyLocal(refundsMonthToDate), color: 'text-amber-600' },
                    { label: 'Refunds YTD', value: formatCurrencyLocal(refundsToDate), color: 'text-amber-600' }
                ]}
            >
                {/* ... table content remains same as previous view ... */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                            <tr>
                                <th className="px-6 py-4">Record & Details</th>
                                <th className="px-6 py-4">Recipient</th>
                                <th className="px-6 py-4">Amount</th>
                                <th className="px-6 py-4">Compliance</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Ops</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                            {categorized.invoices.map(tx => (
                                <tr key={tx.id} className="hover:bg-slate-50/50 group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center font-black text-[10px] text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">INV</div>
                                            <div>
                                                <p className="font-black text-slate-900 uppercase tracking-tighter">{tx.invoiceId || tx.id}</p>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[120px]">{tx.description || 'Service Invoice'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="font-bold text-slate-700">{tx.clientName}</p>
                                        <p className="text-[10px] text-slate-400 font-medium lowercase">{tx.clientEmail}</p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="font-black text-slate-900 text-base">{formatCurrencyLocal(tx.amount)}</p>
                                        {tx.balance > 0 && <p className={`text-[10px] font-black uppercase tracking-tighter ${tx.balance >= tx.amount ? 'text-rose-600' : 'text-amber-600'}`}>Due: {formatCurrencyLocal(tx.balance)}</p>}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <p className="text-xs text-slate-500 font-bold">{tx.dueDate}</p>
                                                {tx.dunningLevel > 0 && (
                                                    <p className={`text-[9px] font-black uppercase tracking-widest mt-1 px-2 py-0.5 rounded-full inline-block ${tx.dunningLevel >= 3 ? 'bg-rose-100 text-rose-600' : tx.dunningLevel === 2 ? 'bg-orange-100 text-orange-600' : 'bg-amber-100 text-amber-600'}`}>
                                                        Stage {tx.dunningLevel}
                                                    </p>
                                                )}
                                            </div>
                                            {tx.status === 'Paid' && (
                                                <div className="bg-emerald-50 p-1.5 rounded-lg border border-emerald-100" title="Audited & Verified">
                                                    <CheckCircle2 size={12} className="text-emerald-600" />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${tx.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : (tx.status?.toLowerCase().includes('pending') || tx.status?.toLowerCase().includes('sent')) ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{tx.status}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-1.5 transition-opacity">
                                            <button onClick={() => handleViewDetails(tx)} className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white" title="View Details"><FileText size={16} /></button>
                                            <button onClick={() => setActiveModal({ type: 'create_invoice', transaction: tx })} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Edit"><Edit size={16} /></button>
                                            {viewMode === 'management' && (tx.status?.toLowerCase().includes('pending') || tx.popLink) && (
                                                <button onClick={() => handleAction('approve', tx)} className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-600 hover:text-white" title="Verify Payment"><CheckCircle2 size={16} /></button>
                                            )}
                                            {viewMode === 'management' && role === 'ADMIN' && (
                                                <>
                                                    <button onClick={() => handleAction('refund', tx)} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Refund"><DollarSign size={16} /></button>
                                                    <button onClick={() => handleAction('delete', tx)} className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white" title="Delete"><Trash2 size={16} /></button>
                                                    <button onClick={() => handleAction('void', tx)} className="p-2.5 bg-slate-100 text-slate-500 rounded-xl hover:bg-slate-800 hover:text-white" title="Void"><XCircle size={16} /></button>
                                                </>
                                            )}
                                            {viewMode === 'personal' && (tx.status !== 'Paid') && (
                                                <label className="cursor-pointer p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white shadow-sm">
                                                    <Upload size={16} />
                                                    <input type="file" className="hidden" onChange={(e) => handlePopUpload(e, tx)} accept="image/*,application/pdf" />
                                                </label>
                                            )}
                                            {tx.popLink && <a href={tx.popLink} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white" title="View POP"><ExternalLink size={16} /></a>}
                                            <button className="p-2.5 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white" title="Download"><Download size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Commissions Section (For Partners & Admin) */}
            {
                (['ADMIN', 'PARTNER'].includes(role) || viewMode === 'management') && (
                    <Section
                        title="Commissions & Referrals"
                        count={categorized.commissions.length}
                        icon={<Users />}
                        color="amber"
                        isOpen={expandedSections.commissions}
                        onToggle={() => toggleSection('commissions')}
                        metrics={[
                            { label: 'Pending Payout', value: formatCurrencyLocal(categorized.commissions.filter(c => (c.status || '').toLowerCase() === 'pending').reduce((s, c) => s + (parseAmt(c.Commission_Amount) || 0), 0)), color: 'text-amber-600' },
                            { label: 'Total Earnings', value: formatCurrencyLocal(metrics.totalCommission) }
                        ]}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                                    <tr>
                                        <th className="px-6 py-4">Partner</th>
                                        <th className="px-6 py-4">Source Transaction</th>
                                        <th className="px-6 py-4">Commission</th>
                                        <th className="px-6 py-4">Status</th>
                                        {role === 'ADMIN' && (
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {categorized.commissions.map((c, i) => (
                                        <tr key={i} className="hover:bg-slate-50/50">
                                            <td className="px-6 py-4">
                                                <p className="font-black text-slate-900 lowercase">{c.Partner_Email || c.referrerEmail}</p>
                                            </td>
                                            <td className="px-6 py-4 text-slate-500 font-medium">#{c.invoiceId || c.id} - {formatCurrencyLocal(c.amount)}</td>
                                            <td className="px-6 py-4 font-black text-emerald-600">{formatCurrencyLocal(c.Commission_Amount)}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${c.status === 'Cleared' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.status || 'Pending'}</span>
                                            </td>
                                            {role === 'ADMIN' && (
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-1.5">
                                                        <button onClick={() => setActiveModal({ type: 'create_invoice', transaction: c })} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Edit"><Edit size={16} /></button>
                                                        <button onClick={() => handleAction('delete', c)} className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white" title="Delete"><Trash2 size={16} /></button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )
            }

            {/* Quotes Section */}
            <Section
                title="Proposals & Quotes"
                count={categorized.quotes.length}
                icon={<Briefcase />}
                color="amber"
                isOpen={expandedSections.quotes}
                onToggle={() => toggleSection('quotes')}
                metrics={[
                    { label: 'Pipeline Value', value: formatCurrencyLocal(quoteMetrics.total) },
                    { label: 'Active Proposals', value: quoteMetrics.active }
                ]}
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                            <tr>
                                <th className="px-6 py-4">Quote #</th>
                                <th className="px-6 py-4">Client</th>
                                <th className="px-6 py-4">Value</th>
                                <th className="px-6 py-4">Issue Date</th>
                                <th className="px-6 py-4">Expiry</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                            {categorized.quotes.map(tx => (
                                <tr key={tx.id} className="hover:bg-slate-50/50 group">
                                    <td className="px-6 py-4 font-black text-slate-900 uppercase tracking-tighter">{tx.invoiceId || tx.id}</td>
                                    <td className="px-6 py-4 text-slate-500 font-bold">{tx.clientName}</td>
                                    <td className="px-6 py-4 font-black text-slate-900">{formatCurrencyLocal(tx.amount)}</td>
                                    <td className="px-6 py-4 text-slate-400">{tx.date}</td>
                                    <td className="px-6 py-4 text-amber-600 font-black">{tx.dueDate}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <button onClick={() => handleViewDetails(tx)} className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white" title="View Details"><FileText size={16} /></button>
                                            <button onClick={() => setActiveModal({ type: 'create_invoice', transaction: tx })} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Edit"><Edit size={16} /></button>
                                            {role === 'ADMIN' && (
                                                <button onClick={() => handleAction('delete', tx)} className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white" title="Delete"><Trash2 size={16} /></button>
                                            )}
                                            {(tx.status?.toLowerCase().includes('sent') || tx.status?.toLowerCase().includes('active')) && (
                                                <button onClick={() => handleAction('accept_quote', tx)} className="px-5 py-2.5 bg-emerald-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-100">
                                                    <Zap size={14} className="fill-current" /> Accept & Convert
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Receipts Section */}
            <Section
                title="Settled Transactions"
                count={categorized.receipts.length}
                icon={<CheckCircle2 />}
                color="emerald"
                isOpen={expandedSections.receipts}
                onToggle={() => toggleSection('receipts')}
                metrics={[
                    { label: 'Net Inflow', value: formatCurrencyLocal(categorized.receipts.reduce((s, r) => s + r.amount, 0)), color: 'text-emerald-600' },
                    { label: 'Total Credits', value: formatCurrencyLocal(totalUnallocatedCredits), color: 'text-indigo-600' }
                ]}
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                            <tr>
                                <th className="px-6 py-4">Receipt #</th>
                                <th className="px-6 py-4">Source</th>
                                <th className="px-6 py-4">Amount</th>
                                <th className="px-6 py-4">Settlement Date</th>
                                <th className="px-6 py-4">Method & Ref</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                            {categorized.receipts.map(tx => (
                                <tr key={tx.id} className="hover:bg-slate-50/50">
                                    <td className="px-6 py-4 font-black text-slate-900 uppercase tracking-tighter">{tx.id}</td>
                                    <td className="px-6 py-4">
                                        <p className="text-slate-500 font-bold">{tx.clientName}</p>
                                        {tx.allocatedToInvoice ? (
                                            <p className="text-[10px] font-bold text-emerald-600 mt-1">
                                                Allocated to: #{tx.allocatedToInvoice}
                                            </p>
                                        ) : (
                                            <p className="text-[10px] font-bold text-amber-600 mt-1">
                                                Unallocated (Account Credit)
                                            </p>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 font-black text-emerald-600 text-lg">{formatCurrencyLocal(tx.amount)}</td>
                                    <td className="px-6 py-4 text-slate-500 font-medium">{tx.date}</td>
                                    <td className="px-6 py-4">
                                        <p className="text-[10px] font-black uppercase text-slate-500">{tx.method || 'Direct EFT'}</p>
                                        <p className="text-[10px] text-slate-300 italic">#{tx.reference || 'SYSTEM_MATCH'}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button onClick={() => handleViewDetails(tx)} className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white" title="View Details"><FileText size={16} /></button>
                                        <button onClick={() => setActiveModal({ type: 'create_invoice', transaction: tx })} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Edit"><Edit size={16} /></button>
                                        {role === 'ADMIN' && (
                                            <button onClick={() => handleAction('delete', tx)} className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white" title="Delete"><Trash2 size={16} /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Commissions Section */}
            {
                (role === 'ADMIN' || role === 'PARTNER' || role === 'COACH') && (
                    <Section
                        title="Commission Performance"
                        count={categorized.commissions.length}
                        icon={<TrendingUp />}
                        color="indigo"
                        isOpen={expandedSections.commissions}
                        onToggle={() => toggleSection('commissions')}
                        metrics={[{ label: 'Total Earned', value: formatCurrencyLocal(metrics.totalCommission), color: 'text-indigo-600' }]}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                                    <tr>
                                        <th className="px-6 py-4">Transaction ID</th>
                                        <th className="px-6 py-4">Source Client</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4">Net Earned</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {categorized.commissions.map(tx => (
                                        <tr key={tx.id} className="hover:bg-slate-50/50">
                                            <td className="px-6 py-4 font-black text-slate-900 uppercase tracking-tighter">{tx.id}</td>
                                            <td className="px-6 py-4 text-slate-500 font-bold">{tx.clientName}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${tx.status?.toLowerCase().includes('cleared') || tx.status?.toLowerCase().includes('paid') ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                                    {tx.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-slate-400 font-medium">{tx.date}</td>
                                            <td className="px-6 py-4 font-black text-indigo-600 text-lg">
                                                {formatCurrencyLocal(tx.Commission_Amount || tx.amount)}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button onClick={() => handleViewDetails(tx)} className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white" title="View Details"><FileText size={16} /></button>
                                                <button onClick={() => setActiveModal({ type: 'create_invoice', transaction: tx })} className="p-2.5 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white" title="Edit"><Edit size={16} /></button>
                                                {role === 'ADMIN' && (
                                                    <button onClick={() => handleAction('delete', tx)} className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white" title="Delete"><Trash2 size={16} /></button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )
            }

            {/* Modals */}
            <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} invoice={selectedTx} onSuccess={fetchData} />
            {(activeModal === 'create_invoice' || (activeModal?.type === 'create_invoice')) && <InvoiceModal onClose={() => setActiveModal(null)} transaction={activeModal?.transaction || null} />}
            {activeModal === 'log_expense' && <LogExpenseModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'gen_statement' && <StatementModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'log_payment' && <LogPaymentModal onClose={() => setActiveModal(null)} />}
            {transactionDetailModalOpen && (
                <TransactionDetailModal onClose={() => setTransactionDetailModalOpen(false)} transaction={selectedTxForDetail} onSuccess={fetchData} />
            )}

            {/* Full-Screen Upload Overlay */}
            {
                uploading && (
                    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex flex-col items-center justify-center text-white p-10 text-center">
                        <div className="w-24 h-24 bg-indigo-600 rounded-3xl flex items-center justify-center mb-8 shadow-2xl animate-bounce">
                            <Upload size={40} />
                        </div>
                        <h2 className="text-4xl font-black uppercase tracking-tighter mb-2">Syncing Proof...</h2>
                        <p className="text-indigo-200 font-bold uppercase tracking-widest text-xs">Direct to Sovereign Ledger</p>
                    </div>
                )
            }
        </div>
    );
};

const MetricCard = ({ title, value, icon, color, bgColor, trend }: any) => (
    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm hover:shadow-2xl transition-all duration-500 group">
        <div className={`w-14 h-14 ${bgColor} ${color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>{icon}</div>
        <p className="text-3xl font-black text-slate-900 tracking-tighter">{value}</p>
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mt-1">{title}</p>
        <p className="mt-4 pt-4 border-t border-slate-50 text-[10px] font-black text-slate-300 italic uppercase">
            {trend}
        </p>
    </div>
);

const Section = ({ title, count, icon, color, isOpen, onToggle, metrics, children }: any) => {
    const colorClasses = {
        indigo: 'bg-indigo-600 shadow-indigo-200',
        amber: 'bg-amber-500 shadow-amber-200',
        emerald: 'bg-emerald-600 shadow-emerald-200'
    };

    return (
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden transition-all">
            <div className="p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div onClick={onToggle} className="flex items-center gap-6 cursor-pointer group">
                    <div className={`w-16 h-16 ${colorClasses[color]} text-white rounded-[2rem] flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform`}>
                        {React.cloneElement(icon, { size: 28 })}
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                            {title}
                            <ChevronDown className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} />
                        </h2>
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">{count} Total Records</p>
                    </div>
                </div>

                {isOpen && metrics && (
                    <div className="flex flex-wrap gap-8 md:gap-12 animate-in fade-in slide-in-from-right-4 duration-500">
                        {metrics.map((m, i) => (
                            <div key={i} className="text-right">
                                <p className={`text-xl font-black ${m.color || 'text-slate-900'} tracking-tighter`}>{m.value}</p>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{m.label}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isOpen && (
                <div className="border-t border-slate-50 p-2 animate-in zoom-in-95 duration-300">
                    {children}
                </div>
            )}
        </div>
    );
};

export default BillingHubView;
